import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { eventsApi } from '../api/events.js';
import { API_BASE } from '../api/client.js';
import { errorCodeOf, toUserMessage } from '../api/errors.js';
import { Loading } from '../components/Loading.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Tag } from '../components/Tag.js';
import { SiteFooter } from '../components/SiteFooter.js';
import { schoolFullLabel } from '../lib/format.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';
import type { EventDto, FeedDto, MilestoneDto } from '../api/types.js';

function formatDueAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
}

function statusBadge(status: string | null | undefined): { text: string; kind: string } {
  switch (status) {
    case 'open':
      return { text: '报名中', kind: 'open' };
    case 'closed':
      return { text: '已截止', kind: 'closed' };
    default:
      return { text: '进行中', kind: 'open' };
  }
}

function DaysLeft({ days }: { days: number | null }) {
  if (days === null) return <span className="ev-days muted">—</span>;
  if (days < 0) return <span className="ev-days ev-days--past">已过期</span>;
  if (days === 0) return <span className="ev-days ev-days--soon">今天截止</span>;
  return <span className="ev-days">{days} 天后截止</span>;
}

function MilestoneRow({ m }: { m: MilestoneDto }) {
  const past = m.daysLeft !== null && m.daysLeft < 0;
  return (
    <li className="timeline-item">
      <span className={`timeline-dot ${past ? 'hollow' : 'solid'}`} />
      <div className="timeline-body">
        <div className="ev-ms-head">
          <span className="ev-ms-label">{m.label}</span>
          <DaysLeft days={m.daysLeft} />
        </div>
        <span className="muted small">{formatDueAt(m.dueAt)}</span>
      </div>
    </li>
  );
}

function FeedItem({ f }: { f: FeedDto }) {
  return (
    <li className="ev-feed">
      <a href={f.url} target="_blank" rel="noreferrer noopener" className="ev-feed-title">
        {f.title}
      </a>
      {f.summary ? <p className="muted small ev-feed-sum">{f.summary}</p> : null}
      <span className="muted small ev-feed-src">{f.source}</span>
    </li>
  );
}

export function EventPublicPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authed = useSession((state) => Boolean(state.accessToken) || Boolean(state.refreshToken));

  const timelineQ = useQuery({
    queryKey: ['event-public', code],
    queryFn: () => eventsApi.timelinePublic(code),
  });

  const listQ = useQuery({
    queryKey: ['events-public-list'],
    queryFn: () => eventsApi.listPublic(),
  });

  const join = useMutation({
    mutationFn: () => eventsApi.join(code),
    onSuccess: () => {
      toast.success('已加入赛事', '可在「我的队伍」中查看与管理');
      void queryClient.invalidateQueries({ queryKey: ['event-public', code] });
    },
    onError: (error) => {
      toast.error(`加入失败：${toUserMessage(error)}`);
    },
  });

  if (timelineQ.isLoading) {
    return (
      <div className="auth-shell">
        <Loading label="正在加载赛事信息…" rows={3} />
      </div>
    );
  }

  if (timelineQ.isError) {
    const errCode = errorCodeOf(timelineQ.error);
    return (
      <div className="auth-shell">
        <div className="ev-shell">
          <Card title={errCode === 'NOT_FOUND' ? '赛事不存在' : '加载失败'}>
            <p className="field-hint">
              {errCode === 'NOT_FOUND'
                ? `赛事码「${code}」不存在或已删除。`
                : `读取赛事信息出错：${toUserMessage(timelineQ.error)}`}
            </p>
            <div className="row">
              <Button variant="ghost" onClick={() => navigate('/')}>
                返回首页
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const { event, milestones, feeds } = timelineQ.data as {
    event: EventDto;
    milestones: MilestoneDto[];
    feeds: FeedDto[];
  };
  const badge = statusBadge(event.status);
  const others = (listQ.data?.events ?? []).filter((e) => e.code !== event.code).slice(0, 6);
  const schools = (event.schools ?? []).filter((s) => Boolean(s));
  const isExclusive = event.scope === 'exclusive';

  return (
    <div className="auth-shell">
      <div className="ev-shell">
        <section className="ev-hero card">
          <div className="ev-hero-top">
            <span className="ev-code">{event.code}</span>
            <span className={`ev-status ev-status--${badge.kind}`}>{badge.text}</span>
            {isExclusive ? (
              <Tag tone="brand" title="仅下列学校可参与">
                某校专属
              </Tag>
            ) : (
              <Tag tone="ok" title="多校共同参与">
                跨校共性
              </Tag>
            )}
          </div>
          <h1 className="ev-title">{event.name}</h1>
          <div className="ev-meta">
            {event.season ? <span>赛季 {event.season}</span> : null}
            {typeof event.memberCount === 'number' ? <span>· {event.memberCount} 人已参与</span> : null}
          </div>
          {schools.length > 0 ? (
            <div className="ev-meta">
              <span>参与学校：{schools.map((s) => schoolFullLabel(s as never)).join('、')}</span>
            </div>
          ) : null}
          <div className="ev-actions">
            {authed ? (
              <Button
                variant="primary"
                loading={join.isPending}
                onClick={() => join.mutate()}
              >
                {event.status === 'closed' ? '赛事已截止' : '加入赛事'}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => navigate('/login', { state: { from: `/e/${event.code}` } })}>
                登录后加入
              </Button>
            )}
            <a
              className="btn btn-secondary"
              href={`${API_BASE}${eventsApi.calendarIcsPath(event.code)}`}
              target="_blank"
              rel="noreferrer"
            >
              添加到日历
            </a>
            <Link className="btn btn-ghost" to="/register">
              注册账号
            </Link>
          </div>
        </section>

        <div className="ev-grid">
          <div className="ev-col-main">
            <Card title="赛事里程碑">
              {milestones.length === 0 ? (
                <p className="field-hint">暂无里程碑，组织方尚未设置赛程。</p>
              ) : (
                <ul className="timeline">
                  {milestones.map((m) => (
                    <MilestoneRow key={m.id} m={m} />
                  ))}
                </ul>
              )}
            </Card>

            <Card title="赛事动态" subtitle="官方公告与赛事相关公开信息">
              {feeds.length === 0 ? (
                <p className="field-hint">暂无动态。</p>
              ) : (
                <ul className="ev-feeds">
                  {feeds.map((f) => (
                    <FeedItem key={f.id} f={f} />
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <div className="ev-col-side">
            <EventRequirements code={event.code} />
            {others.length > 0 ? (
              <Card title="其他在招赛事">
                <ul className="ev-others">
                  {others.map((e) => (
                    <li key={e.code}>
                      <Link to={`/e/${e.code}`}>{e.name}</Link>
                      <span className="muted small"> · {e.code}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        </div>

        <p className="muted small ev-foot-note">
          加入赛事即表示同意平台《用户协议》与《隐私政策》。CrossMatch 仅提供组队匹配基础设施，赛事规则以主办方为准。
        </p>
      </div>
      <SiteFooter />
    </div>
  );
}

function EventRequirements({ code }: { code: string }) {
  const reqQ = useQuery({
    queryKey: ['event-requirements-public', code],
    queryFn: () => eventsApi.requirementsPublic(code),
  });
  return (
    <Card title="招募技能需求">
      {reqQ.isLoading ? (
        <p className="field-hint">加载中…</p>
      ) : reqQ.isError ? (
        <p className="field-hint">需求暂不可读</p>
      ) : reqQ.data?.requirements.length === 0 ? (
        <p className="field-hint">主办方尚未发布技能需求。</p>
      ) : (
        <div className="req-chips">
          {reqQ.data?.requirements.map((r) => (
            <span key={r.skill} className="req-chip">
              {r.skill}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}
